/**
 * House-aware phaladesh for Drishti calculator (yuti + संक्षिप्त फल).
 * Draws on Takshify planet-in-house notes, graha-bhava fit, and Parashari teaching threads.
 */
import { getBhavaForHouse } from './jyotishaBhavaHouses';
import { drishtiPlacementPhalaBody, drishtiPlacementPhalaLine } from './jyotishaDrishtiPlacementPhala';
import { GRAHA_HI_EN_BY_EN } from './jyotishaGrahaNames';
import { GRAHA_BHAVA_FIT_ROWS } from './jyotishaMyReadingGrahaBhavaFit';
import { houseCountOrdinalHi, houseTopicHi } from './jyotishaHouseOrdinalHi';

const MALEFIC = new Set(['Sun', 'Mars', 'Saturn', 'Rahu', 'Ketu']);

function pairKey(a, b) {
  return [a, b].sort().join('|');
}

function grahaLabel(grahaEn) {
  const g = GRAHA_HI_EN_BY_EN[grahaEn];
  return g ? `${g.hi} (${g.en})` : grahaEn;
}

function houseNature(house) {
  if ([6, 8, 12].includes(house)) return 'dusthana';
  if ([1, 4, 7, 10].includes(house)) return 'kendra';
  if ([5, 9].includes(house)) return 'trikona';
  if ([3, 6, 10, 11].includes(house)) return 'upachaya';
  if ([2, 7].includes(house)) return 'maraka';
  return 'neutral';
}

const HOUSE_NATURE_PHALA = {
  dusthana: {
    hi: 'दुःस्थान में युति/दृष्टि अक्सर परीक्षा, ऋण-रोग-व्यय के रंग में - बल व दशा से फल बदलता है।',
    en: 'In a dusthana, yuti or drishti often colours results through struggle, health, debt, or expense - dignity and dasha still modify the outcome.',
  },
  kendra: {
    hi: 'केन्द्र में परिणाम जीवन के मुख्य क्षेत्रों में स्पष्ट दिखते हैं (स्व, सुख, विवाह, कर्म)।',
    en: 'In a kendra, results tend to show clearly in core life areas (self, home, partnership, career).',
  },
  trikona: {
    hi: 'त्रिकोण में धर्म, भाग्य, पुण्य की ओर अनुकूलता बढ़ सकती है।',
    en: 'In a trikona, dharma, fortune, and merit themes may gain support.',
  },
  upachaya: {
    hi: 'उपचय में समय व मेहनत से फल बढ़ने की प्रवृत्ति - विशेषकर पाप ग्रह यहाँ काम कर सकते हैं।',
    en: 'In upachaya, results often grow with time and effort - malefics can work constructively here.',
  },
  maraka: {
    hi: 'मारक भाव में संबंध/धन पर अतिरिक्त दबाव या खर्च के संकेत संभव।',
    en: 'Maraka houses may add pressure on relationships, speech, or expenditure.',
  },
  neutral: {
    hi: 'भाव स्वामी, बल और दशा से अंतिम फल तय होता है।',
    en: 'Final results still depend on the house lord, dignity, and dasha.',
  },
};

/** Two-graha yuti phala (Parashari threads + course yuti lesson). */
const YUTI_PAIR_PHALA = {
  'Moon|Sun': {
    hi: 'सूर्य-चंद्र: इच्छा व मन एक साथ - आत्मविश्वास मजबूत पर मन कभी दबाव में; माता/घर विषय संवेदनशील।',
    en: 'Sun–Moon: will and mind together - confidence with inner tension; mother/home themes sensitive.',
  },
  'Mars|Sun': {
    hi: 'सूर्य-मंगल: साहस, नेतृत्व, ऊर्जा अधिक - क्रोध/अहंकार संयम से नियंत्रित करें।',
    en: 'Sun–Mars: courage, leadership, high drive - channel anger and ego with discipline.',
  },
  'Saturn|Sun': {
    hi: 'सूर्य-शनि: अहंकार पर अनुशासन - पिता/अधिकारी से दूरी या जिम्मेदारी का भार संभव।',
    en: 'Sun–Saturn: discipline on ego - distance or duty from father/authority figures possible.',
  },
  'Jupiter|Sun': {
    hi: 'सूर्य-गुरु: धर्म, ज्ञान, मान - गुरु/पिता से सहायक संकेत; अहंकार में उदारता।',
    en: 'Sun–Jupiter: dharma, wisdom, respect - supportive teachers/father figures; generous pride.',
  },
  'Sun|Venus': {
    hi: 'सूर्य-शुक्र: विलास, कला, संबंध - सार्वजनिक आकर्षण; कभी अति-भोग या मान-प्रेम टकराव।',
    en: 'Sun–Venus: comfort, art, relationships - public charm; watch excess or ego in love.',
  },
  'Mercury|Sun': {
    hi: 'सूर्य-बुध: बुद्धि, वाणी, व्यापार - तेज़ सोच; सूर्य के निकट हो तो अस्त संकेत देखें।',
    en: 'Sun–Mercury: intellect, speech, trade - sharp mind; check combustion if very close to Sun.',
  },
  'Moon|Rahu': {
    hi: 'चंद्र-राहु (ग्रहण): मन अशांत, भावनात्मक उतार-चढ़ाव - विदेश/अनूठे अनुभव की झुकाव।',
    en: 'Moon–Rahu: restless mind, emotional swings - foreign or unusual experiences likely.',
  },
  'Moon|Saturn': {
    hi: 'चंद्र-शनि: मन पर दबाव, गंभीरता - धैर्य से स्थिरता; उदासी संकेत संभव।',
    en: 'Moon–Saturn: mental pressure, seriousness - stability through patience; melancholy possible.',
  },
  'Jupiter|Moon': {
    hi: 'चंद्र-गुरु: मन शांत, धर्म-भावना - माता/शिक्षा से सहायक; भावनात्मक पोषण अच्छा।',
    en: 'Moon–Jupiter: calmer mind, faith - support from mother/education; emotional nourishment.',
  },
  'Moon|Venus': {
    hi: 'चंद्र-शुक्र: कोमल मन, कला/सुख - प्रेम-कविता की झुकाव; भावनात्मक संतुष्टि।',
    en: 'Moon–Venus: gentle mind, art/comfort - romantic and aesthetic leanings.',
  },
  'Mars|Moon': {
    hi: 'चंद्र-मंगल: भावनाएँ तीव्र, साहस - चंचल मन; क्रोध जल्दी उठ सकता है।',
    en: 'Moon–Mars: intense feelings, courage - restless mind; anger can flare quickly.',
  },
  'Mars|Rahu': {
    hi: 'मंगल-राहु (अंगारक): तीव्र ऊर्जा, साहस या विवाद - नियंत्रण व दशा जरूरी।',
    en: 'Mars–Rahu (Angarak): sharp energy, bold or conflictual - needs control and timing.',
  },
  'Mars|Saturn': {
    hi: 'मंगल-शनि: परिश्रम, रुकावट के बाद सफलता - क्रोध व धैर्य का मिश्रण।',
    en: 'Mars–Saturn: effort, success after blocks - blend of drive and restraint.',
  },
  'Jupiter|Mars': {
    hi: 'मंगल-गुरु: धर्म व कार्य में साहस - न्यायपूर्ण नेतृत्व; गुरु मंगल को नरम करता है।',
    en: 'Mars–Jupiter: courage in dharma and work - principled leadership; Jupiter softens Mars.',
  },
  'Mars|Venus': {
    hi: 'मंगल-शुक्र: काम-प्रेम में तीव्रता - रचनात्मक ऊर्जा या जल्दबाज़ी संबंध।',
    en: 'Mars–Venus: passion in desire/love - creative drive or impulsive bonds.',
  },
  'Mercury|Mars': {
    hi: 'मंगल-बुध: तेज़ बुद्धि, वाणी में तीक्ष्णता - व्यापार/तकनीक में फुर्ती।',
    en: 'Mars–Mercury: quick wit, sharp speech - agility in trade/tech.',
  },
  'Jupiter|Mercury': {
    hi: 'बुध-गुरु: विद्या, लेखन, व्यापार-धर्म - बुद्धिमत्ता व विवेक का मेल।',
    en: 'Mercury–Jupiter: learning, writing, ethical commerce - wisdom meets skill.',
  },
  'Mercury|Venus': {
    hi: 'बुध-शुक्र: कला, वाणी, सौंदर्य - संचार व रिश्तों में चातुर्य।',
    en: 'Mercury–Venus: art, speech, beauty - clever communication and charm.',
  },
  'Jupiter|Venus': {
    hi: 'गुरु-शुक्र (लक्ष्मी संकेत): धन, सुख, संस्कार - शुभ युति; विलास में संयम लाभकारी।',
    en: 'Jupiter–Venus (Lakshmi hint): wealth, comfort, culture - auspicious blend; moderation in luxury helps.',
  },
  'Saturn|Venus': {
    hi: 'शुक्र-शनि: प्रेम में गंभीरता/विलंब - दीर्घकालिक बंधन की संभावना।',
    en: 'Venus–Saturn: seriousness or delay in love - long-term bond tendency.',
  },
  'Rahu|Venus': {
    hi: 'शुक्र-राहु: विलास, कला में आकर्षण - संबंधों में तीव्र इच्छा या असामान्यता।',
    en: 'Venus–Rahu: strong pull to pleasure/art - intense or unusual relationships.',
  },
  'Jupiter|Rahu': {
    hi: 'गुरु-राहु (गुरु चांडाल संकेत): धर्म/गुरु में अनूठा या विवादास्पद मार्ग।',
    en: 'Jupiter–Rahu (Guru Chandal hint): unconventional or debated spiritual path.',
  },
  'Jupiter|Saturn': {
    hi: 'गुरु-शनि: धर्म व कर्म का मेल - धीरे पर स्थिर उन्नति; वृद्ध/गुरु से लाभ।',
    en: 'Jupiter–Saturn: dharma meets karma - slow but steady rise; elders/teachers help.',
  },
  'Saturn|Mercury': {
    hi: 'बुध-शनि: गंभीर सोच, व्यावहारिक योजना - विलंबित निर्णय पर धैर्य।',
    en: 'Mercury–Saturn: serious thinking, practical planning - patience with delayed decisions.',
  },
  'Rahu|Saturn': {
    hi: 'शनि-राहु: कर्म में अचानक मोड़ - अनुशासन व भ्रम की दोहरी परीक्षा।',
    en: 'Saturn–Rahu: sudden karmic turns - discipline tested alongside confusion.',
  },
  'Ketu|Sun': {
    hi: 'सूर्य-केतु: आत्म-विश्लेषण, आध्यात्म - पिता/सत्ता में रहस्य या अलगाव।',
    en: 'Sun–Ketu: introspection, spirituality - mystery or detachment around father/authority.',
  },
  'Rahu|Sun': {
    hi: 'सूर्य-राहु: ग्रहण जैसी तीव्रता - अहंकार, पिता/अधिकारी विषय उतार-चढ़ाव।',
    en: 'Sun–Rahu: eclipse-like intensity - ego and authority themes swing sharply.',
  },
  'Ketu|Moon': {
    hi: 'चंद्र-केतु: मन अंतर्मुखी - भावनात्मक अलगाव या आध्यात्मिक खोज।',
    en: 'Moon–Ketu: inward mind - emotional detachment or spiritual seeking.',
  },
  'Ketu|Mars': {
    hi: 'मंगल-केतु: ऊर्जा आध्यात्म या विवाद की ओर - अचानक क्रियाएँ संभव।',
    en: 'Mars–Ketu: energy toward spirit or conflict - sudden actions possible.',
  },
  'Jupiter|Ketu': {
    hi: 'गुरु-केतु: गूढ़ ज्ञान, मोक्ष-झुकाव - सांसारिक लक्ष्य कम, आंतरिक खोज अधिक।',
    en: 'Jupiter–Ketu: hidden wisdom, moksha lean - less worldly, more inner search.',
  },
  'Ketu|Saturn': {
    hi: 'शनि-केतु: कर्म-मोक्ष का मिश्रण - एकांत, त्याग, पुराने कर्म का भोग।',
    en: 'Saturn–Ketu: karma–moksha mix - solitude, renunciation, old karma ripening.',
  },
  'Ketu|Rahu': {
    hi: 'राहु-कetu एक साथ नहीं बैठते (अक्ष अमूर्त) - यह संयोजन लग्न चार्ट में नहीं होता।',
    en: 'Rahu and Ketu are never conjunct (opposite axis) - not a valid same-house pair.',
  },
};

/** Per-graha flavour when aspecting a house (whole-sign drishti). */
const DRISHTI_GRAHA_PHALA = {
  Sun: {
    hi: 'सत्ता, पिता, आत्मविश्वास के विषय प्रभावित - उज्ज्वल या दबाव दोनों संभव।',
    en: 'Themes of authority, father, self-esteem - can brighten or pressurize.',
  },
  Moon: {
    hi: 'मन, माता, सुख पर असर - भावनात्मक लहरें बढ़ सकती हैं।',
    en: 'Affects mind, mother, comfort - emotional tides may rise.',
  },
  Mars: {
    hi: 'साहस, प्रतिस्पर्धा, तीव्रता - संघर्ष या सक्रिय कार्य की ओर।',
    en: 'Courage, competition, heat - toward struggle or active effort.',
  },
  Mercury: {
    hi: 'बुद्धि, वाणी, व्यापार - संचार व तर्क में गतिविधि।',
    en: 'Intellect, speech, trade - activity in communication and logic.',
  },
  Jupiter: {
    hi: 'विस्तार, धर्म, गुरु-कृपा - सहायक व सुरक्षात्मक झुकाव (बल पर निर्भर)।',
    en: 'Expansion, dharma, grace - tends supportive when strong.',
  },
  Venus: {
    hi: 'सुख, प्रेम, कला - सौंदर्य व संबंधों में वृद्धि या विलास।',
    en: 'Comfort, love, art - growth in beauty, bonds, or indulgence.',
  },
  Saturn: {
    hi: 'विलंब, परिश्रम, अनुशासन - धीमे पर स्थायी परिणाम; कठिनाई संभव।',
    en: 'Delay, labour, discipline - slow lasting results; hardship possible.',
  },
  Rahu: {
    hi: 'विस्तार, विदेश, अनूठापन - इच्छा तीव्र; माया/भ्रम संभव।',
    en: 'Amplification, foreign, novelty - strong desire; illusion possible.',
  },
  Ketu: {
    hi: 'विरक्ति, आध्यात्म, कटाव - सांसारिक विषय कम, भीतर की सीख अधिक।',
    en: 'Detachment, spirituality, severing - less worldly, more inner lesson.',
  },
};

function grahaFitNote(grahaEn, house) {
  const row = GRAHA_BHAVA_FIT_ROWS.find((r) => r.planet === grahaEn);
  if (!row) return null;
  if (row.favorable.includes(house)) {
    return {
      hi: `${GRAHA_HI_EN_BY_EN[grahaEn]?.hi ?? grahaEn} को यह भाव अनुकूल माना जाता है - विषय सहजता से खुल सकते हैं।`,
      en: `${grahaEn} is considered well-placed in this house - themes may open more easily.`,
    };
  }
  if (row.challenging.includes(house)) {
    return {
      hi: `${GRAHA_HI_EN_BY_EN[grahaEn]?.hi ?? grahaEn} के लिए यह भाव परीक्षा वाला - अतिरिक्त संयम व दशा देखें।`,
      en: `${grahaEn} finds this house testing - extra care with dasha and remedies.`,
    };
  }
  return null;
}

/** @param {string} grahaEn @param {number} house */
export function grahaInHousePhala(grahaEn, house) {
  const placement = drishtiPlacementPhalaBody(grahaEn, house);
  if (placement) return placement;
  const fit = grahaFitNote(grahaEn, house);
  if (fit) return fit;
  const g = GRAHA_HI_EN_BY_EN[grahaEn];
  return {
    hi: `${houseTopicHi(house)} में ${g?.hi ?? grahaEn} - भाव के विषयों में इस ग्रह की ऊर्जा सक्रिय।`,
    en: `${g?.en ?? grahaEn} in house ${house} activates this bhava's themes.`,
  };
}

/**
 * @param {number} house
 * @param {string[]} grahas
 * @param {{ significanceHi?: string }} [meta]
 */
export function buildYutiPhaladesh(house, grahas, meta) {
  const bhava = meta ?? getBhavaForHouse(house);
  const nature = houseNature(house);
  const naturePhala = HOUSE_NATURE_PHALA[nature];

  /** @type {string[]} */
  const linesHi = [];
  /** @type {string[]} */
  const linesEn = [];

  if (!grahas.length) {
    return {
      linesHi: ['कोई ग्रह नहीं - युति नहीं; खाली भाव का फल मुख्यतः भावेश व दृष्टि से।'],
      linesEn: ['No graha - no yuti; empty house reads mainly through lord and aspects.'],
    };
  }

  if (grahas.length === 1) {
    const p = grahaInHousePhala(grahas[0], house);
    linesHi.push(p.hi);
    linesEn.push(p.en);
    linesHi.push(naturePhala.hi);
    linesEn.push(naturePhala.en);
    return { linesHi, linesEn };
  }

  linesHi.push(
    `${houseTopicHi(house)} में ${grahas.length} ग्रहों की युति - ${bhava?.significanceHi ?? 'इस भाव'} के विषय एक साथ मिलकर प्रकट होंगे।`,
  );
  linesEn.push(
    `${grahas.length}-graha yuti in ${houseTopicHi(house)} - themes of ${bhava?.significanceEn ?? 'this house'} blend together.`,
  );

  for (const g of grahas) {
    const line = drishtiPlacementPhalaLine(g, house);
    if (line) {
      linesHi.push(`• ${line.hi}`);
      linesEn.push(`• ${line.en}`);
      continue;
    }
    const p = grahaInHousePhala(g, house);
    linesHi.push(`• ${grahaLabel(g)}: ${p.hi}`);
    linesEn.push(`• ${grahaLabel(g)}: ${p.en}`);
  }

  const seenPairs = new Set();
  for (let i = 0; i < grahas.length; i++) {
    for (let j = i + 1; j < grahas.length; j++) {
      const key = pairKey(grahas[i], grahas[j]);
      if (seenPairs.has(key)) continue;
      seenPairs.add(key);
      const pair = YUTI_PAIR_PHALA[key];
      if (pair) {
        linesHi.push(`• युति ${grahaLabel(grahas[i])} + ${grahaLabel(grahas[j])}: ${pair.hi}`);
        linesEn.push(`• Yuti ${grahas[i]} + ${grahas[j]}: ${pair.en}`);
      }
    }
  }

  const beneficN = grahas.filter((g) => !MALEFIC.has(g)).length;
  const maleficN = grahas.length - beneficN;
  if (beneficN > 0 && maleficN > 0) {
    linesHi.push(
      '• शुभ + पाप युति: परिणाम मिश्रित - शुक्त ग्रह की दृष्टि/बल से पाप कमजोर हो सकता है (पाठ: नियत नियम · युति)।',
    );
    linesEn.push('• Benefic + malefic yuti: mixed results - strong benefic aspects can soften malefics (see fixed-rules yuti).');
  } else if (maleficN >= 2 && beneficN === 0) {
    linesHi.push('• दो या अधिक पाप ग्रह: संघर्ष/तीव्रता बढ़ सकती है - पर उपचय/केन्द्र में कर्म से सफलता भी।');
    linesEn.push('• Two+ malefics: struggle or intensity rises - yet upachaya/kendra can still yield effort-based success.');
  } else if (beneficN >= 2) {
    linesHi.push('• अधिक शुभ युति: विषयों में सहायता, विस्तार व सौंदर्य की प्रवृत्ति।');
    linesEn.push('• Mostly benefic yuti: support, expansion, and refinement of house themes.');
  }

  if (grahas.includes('Mercury') && grahas.length > 1) {
    linesHi.push('• बुध साथी से रंग लेता है - सबसे निकट ग्रह/युति-स्वामी का स्वभाव देखें।');
    linesEn.push('• Mercury takes colour from companions - weigh the closest graha’s tone.');
  }

  linesHi.push(`• भाव संदर्भ: ${naturePhala.hi}`);
  linesEn.push(`• House context: ${naturePhala.en}`);

  return { linesHi, linesEn };
}

/** Factual drishti line + separate interpretation (for structured UI). */
function drishtiPhalaForAspector(x) {
  const graha = x.kind === 'placement' ? x.graha : x.lord;
  const base = DRISHTI_GRAHA_PHALA[graha] ?? {
    hi: 'इस ग्रह की दृष्टि भाव के विषयों को अपने स्वभाव से रंग देती है।',
    en: 'This graha colours the house by its natural nature.',
  };
  const from = houseCountOrdinalHi(x.fromHouse);
  const labelHi =
    x.kind === 'bhavesh'
      ? `${houseCountOrdinalHi(x.ownedHouse)} के स्वामी ${grahaLabel(graha)} (${from} से ${x.aspectLabel} दृष्टि)`
      : `${grahaLabel(graha)} (${from} से ${x.aspectLabel} दृष्टि)`;
  const labelEn =
    x.kind === 'bhavesh'
      ? `lord of ${houseTopicHi(x.ownedHouse)} ${grahaLabel(graha)} (${x.aspectLabel} from ${houseTopicHi(x.fromHouse)})`
      : `${grahaLabel(graha)} (${x.aspectLabel} from ${houseTopicHi(x.fromHouse)})`;
  return { labelHi, labelEn, bodyHi: base.hi, bodyEn: base.en };
}

function flattenPhaladeshSections(sections) {
  const linesHi = [];
  const linesEn = [];
  for (const s of sections) {
    linesHi.push(s.headingHi);
    linesEn.push(s.headingEn);
    for (const item of s.items) {
      if (item.labelHi) linesHi.push(item.labelHi);
      if (item.labelEn) linesEn.push(item.labelEn);
      linesHi.push(item.bodyHi);
      linesEn.push(item.bodyEn);
    }
  }
  return { linesHi, linesEn };
}

/**
 * @param {number} house
 * @param {string[]} occupants
 * @param {Array<object>} aspectors
 * @param {string} [lord]
 * @param {{ significanceHi?: string; significanceEn?: string }} [meta]
 */
function buildYutiSummaryItems(grahas, house, bhava) {
  /** @type {Array<{ labelHi?: string; labelEn?: string; bodyHi: string; bodyEn: string }>} */
  const items = [];

  for (const g of grahas) {
    const line = drishtiPlacementPhalaLine(g, house);
    if (line) {
      items.push({ bodyHi: line.hi, bodyEn: line.en });
      continue;
    }
    const p = grahaInHousePhala(g, house);
    items.push({
      labelHi: grahaLabel(g),
      labelEn: grahaLabel(g),
      bodyHi: p.hi,
      bodyEn: p.en,
    });
  }

  const seen = new Set();
  for (let i = 0; i < grahas.length; i++) {
    for (let j = i + 1; j < grahas.length; j++) {
      const key = pairKey(grahas[i], grahas[j]);
      if (seen.has(key)) continue;
      seen.add(key);
      const pair = YUTI_PAIR_PHALA[key];
      if (pair) {
        items.push({
          labelHi: `${grahaLabel(grahas[i])} + ${grahaLabel(grahas[j])}`,
          labelEn: `${grahas[i]} + ${grahas[j]}`,
          bodyHi: pair.hi,
          bodyEn: pair.en,
        });
      }
    }
  }
  const beneficN = grahas.filter((g) => !MALEFIC.has(g)).length;
  const maleficN = grahas.length - beneficN;
  if (beneficN > 0 && maleficN > 0) {
    items.push({
      bodyHi: 'शुभ + पाप युति: परिणाम मिश्रित - शुभ ग्रह की दृष्टि/बल से पाप कमजोर हो सकता है।',
      bodyEn: 'Benefic + malefic yuti: mixed - strong benefic aspects can soften malefics.',
    });
  } else if (maleficN >= 2) {
    items.push({
      bodyHi: 'दो या अधिक पाप ग्रह: संघर्ष/तीव्रता बढ़ सकती है - उपचय/केन्द्र में कर्म से फल भी।',
      bodyEn: 'Two+ malefics: more struggle - yet upachaya/kendra can still yield effort-based results.',
    });
  } else if (beneficN >= 2) {
    items.push({
      bodyHi: 'अधिक शुभ युति: विषयों में सहायता, विस्तार व सौंदर्य की प्रवृत्ति।',
      bodyEn: 'Mostly benefic yuti: support, expansion, and refinement of house themes.',
    });
  }
  if (grahas.includes('Mercury') && grahas.length > 1) {
    items.push({
      bodyHi: 'बुध साथी से रंग लेता है - सबसे निकट ग्रह का स्वभाव देखें।',
      bodyEn: 'Mercury adapts to companions - weigh the closest graha’s tone.',
    });
  }
  if (grahas.length >= 3) {
    items.push({
      bodyHi: `${grahas.length} ग्रहों की गाढ़ी युति - अंश निकट हों तो ग्रह युद्ध पाठ में जोड़े-जोड़े से देखें।`,
      bodyEn: `${grahas.length}-graha bundle - if degrees are close, check Grah Yuddha lesson pairwise.`,
    });
  }
  items.push({
    bodyHi: HOUSE_NATURE_PHALA[houseNature(house)].hi,
    bodyEn: HOUSE_NATURE_PHALA[houseNature(house)].en,
  });
  return items;
}

export function buildSummaryPhaladesh(house, occupants, aspectors, lord, meta) {
  const bhava = meta ?? getBhavaForHouse(house);
  const nature = houseNature(house);
  const naturePhala = HOUSE_NATURE_PHALA[nature];

  /** @type {Array<{ id: string; headingHi: string; headingEn: string; items: Array<{ labelHi?: string; labelEn?: string; bodyHi: string; bodyEn: string }> }>} */
  const sections = [];

  if (occupants.length) {
    if (occupants.length === 1) {
      const line = drishtiPlacementPhalaLine(occupants[0], house);
      const p = line ?? grahaInHousePhala(occupants[0], house);
      sections.push({
        id: 'placement',
        headingHi: 'यहाँ बैठे ग्रह का फल',
        headingEn: 'Placed graha - effect',
        items: line
          ? [{ bodyHi: line.hi, bodyEn: line.en }]
          : [{ labelHi: grahaLabel(occupants[0]), labelEn: grahaLabel(occupants[0]), bodyHi: p.hi, bodyEn: p.en }],
      });
    } else {
      sections.push({
        id: 'placement',
        headingHi: 'यहाँ बैठे (युति)',
        headingEn: 'Placed (yuti)',
        items: occupants.map((g) => {
          const line = drishtiPlacementPhalaLine(g, house);
          if (line) return { bodyHi: line.hi, bodyEn: line.en };
          const p = grahaInHousePhala(g, house);
          return {
            labelHi: grahaLabel(g),
            labelEn: grahaLabel(g),
            bodyHi: p.hi,
            bodyEn: p.en,
          };
        }),
      });
      sections.push({
        id: 'yuti',
        headingHi: 'युति का फल (संक्षेप)',
        headingEn: 'Yuti - brief effect',
        items: buildYutiSummaryItems(occupants, house, bhava),
      });
    }
  } else {
    sections.push({
      id: 'placement',
      headingHi: 'खाली भाव',
      headingEn: 'Empty house',
      items: [
        {
          bodyHi: `फल मुख्यतः ${lord ? `${grahaLabel(lord)} (स्वामी)` : 'भावेश'} की स्थिति, दृष्टि व दशा से तय होगा।`,
          bodyEn: `Results mainly from ${lord ? grahaLabel(lord) : 'house lord'} placement, aspects, and dasha.`,
        },
      ],
    });
  }

  if (aspectors.length) {
    sections.push({
      id: 'drishti',
      headingHi: 'दृष्टि का फल (संक्षेप)',
      headingEn: 'Drishti effect (brief)',
      items: aspectors.map((x) => {
        const d = drishtiPhalaForAspector(x);
        return { labelHi: d.labelHi, labelEn: d.labelEn, bodyHi: d.bodyHi, bodyEn: d.bodyEn };
      }),
    });

    const beneficCount = aspectors.filter((x) => !MALEFIC.has(x.kind === 'placement' ? x.graha : x.lord)).length;
    const maleficCount = aspectors.length - beneficCount;
    let netHi;
    let netEn;
    if (beneficCount > maleficCount) {
      netHi = `अधिक शुभ दृष्टि - ${bhava?.significanceHi ?? 'इस भाव'} में सहायता/विस्तार की संभावना (बल व दशा जरूर देखें)।`;
      netEn = `Mostly benefic drishti - support/expansion likely for ${bhava?.significanceEn ?? 'this house'} (check strength and dasha).`;
    } else if (maleficCount > beneficCount) {
      netHi = `अधिक कठिन दृष्टि - ${bhava?.significanceHi ?? 'इस भाव'} में तनाव, बाधा या दबाव संभव; शुभ योग/दशा से नरमी।`;
      netEn = `Mostly challenging drishti - stress or pressure possible; benefic dasha/yogas soften.`;
    } else {
      netHi = 'मिश्रित दृष्टि - शुभ व पाप दोनों का असर; पूरी कुंडली व अंतरदशा से निष्कर्ष।';
      netEn = 'Mixed drishti - both support and stress; judge with full chart and antardasha.';
    }
    sections.push({
      id: 'drishti-net',
      headingHi: 'दृष्टि - सार',
      headingEn: 'Drishti - net',
      items: [{ bodyHi: netHi, bodyEn: netEn }],
    });
  } else {
    sections.push({
      id: 'drishti',
      headingHi: 'दृष्टि का फल (संक्षेप)',
      headingEn: 'Drishti effect (brief)',
      items: [
        {
          bodyHi: 'कोई शास्त्रीय दृष्टि नहीं - भावेश व स्वामी की स्थिति प्रधान।',
          bodyEn: 'No classical drishti - house lord placement is primary.',
        },
      ],
    });
  }

  sections.push({
    id: 'nature',
    headingHi: 'भाव प्रकृति',
    headingEn: 'Bhava nature',
    items: [{ bodyHi: naturePhala.hi, bodyEn: naturePhala.en }],
  });

  const { linesHi, linesEn } = flattenPhaladeshSections(sections);
  return { sections, linesHi, linesEn };
}
